import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'

import LocalStorageButtons from '@components/LocalStorageButtons'
import FilterGridContainer from '@components/toolbar/FilterGridContainer'
import FilterGridItem from '@components/toolbar/FilterGridItem'
import { FilterMyList } from '@components/toolbar/FilterMyList'
import { FilterSelectObject } from '@components/toolbar/FilterSelectObject'
import type { FilterOption, GroupedFilterOption } from '@models/Filter'
import { parseISO } from '@util/date-util'
import { filterVerantwoordelijkeOptionsByOrganisatie } from '@util/project-util'

import { useAuthenticatedUser } from '../../../auth/hooks/useAuthenticatedUser'
import { SelectObject } from '../../../core/components/SelectObject'
import { SelectString } from '../../../core/components/SelectString'
import { useLocalFilters } from '../../../core/hooks/useLocalFilters'
import type { ProcesstapHoofdleiding } from '../../../status/types/ProcesstapHoofdleiding'
import { queryKey } from '../hooks/useTeBeoordelen'
import type { FetchTeBeoordelenFilters } from '../types/FetchTeBeoordelen'

interface Props {
  filters: FetchTeBeoordelenFilters
  initialFilters: FetchTeBeoordelenFilters
  onChange: (filters: FetchTeBeoordelenFilters) => void
  onReset: () => void
  onRefresh: () => void
  filterOptions: {
    coordinerenden: string[]
    labels: FilterOption[]
    hoofdleiding: {
      gebieden: FilterOption[]
      netbeheerders: string[]
      verantwoordelijken: GroupedFilterOption[]
      typeWerkTypes: string[]
      gemeenten: string[]
    }
    processtappen: ProcesstapHoofdleiding[]
  }
}

const buildOption = (group: string) => (label: string) => ({ group, label })

export const TeBeoordelenFilters = ({
  filters,
  initialFilters,
  onChange,
  onReset,
  onRefresh,
  filterOptions: {
    coordinerenden,
    labels: labelOptions,
    hoofdleiding: {
      gebieden,
      netbeheerders,
      verantwoordelijken,
      typeWerkTypes,
      gemeenten,
    },
    processtappen,
  },
}: Props) => {
  const { combi, organisatie: userOrganisatie } = useAuthenticatedUser()
  const assignees = (combi ? [buildOption('Combi')(combi)] : [])
    .concat(netbeheerders.map(buildOption('Netbeheerder')))
    .concat(coordinerenden.map(buildOption('Aannemer')))

  const { localFilters, handleFilterChange, handleZoeken, handleReset } =
    useLocalFilters({
      filters,
      initialFilters,
      onChange: (filters) => onChange(filters),
      onRefresh,
      onReset,
    })
  const {
    gebied,
    organisatie,
    labels,
    myList,
    startUitvoeringWeekTot,
    startUitvoeringWeekVan,
    verantwoordelijke,
    opleverweekTot,
    opleverweekVan,
    processtatus,
    typeWerk,
    gemeente,
  } = localFilters

  const verantwoordelijkeOptions = filterVerantwoordelijkeOptionsByOrganisatie(
    verantwoordelijken,
    userOrganisatie,
  )

  return (
    <FilterGridContainer>
      <FilterGridItem>
        <FilterSelectObject
          options={gebieden}
          label="Gebied"
          filterKey="gebied"
          value={gebied}
          onFilterChange={handleFilterChange}
          multiple
        />
      </FilterGridItem>

      {labelOptions.length > 0 && (
        <FilterGridItem>
          <SelectObject
            multiple
            options={labelOptions}
            label="Labels"
            onChange={(value) => handleFilterChange({ labels: value })}
            value={labels}
          />
        </FilterGridItem>
      )}
      <FilterGridItem>
        <SelectObject
          options={verantwoordelijkeOptions}
          label="Projectverantwoordelijke"
          value={verantwoordelijke}
          onChange={(value) => handleFilterChange({ verantwoordelijke: value })}
        />
      </FilterGridItem>

      <FilterGridItem>
        <SelectString
          multiple
          value={typeWerk}
          options={typeWerkTypes}
          label="Type werk"
          onChange={(value) => handleFilterChange({ typeWerk: value })}
        />
      </FilterGridItem>

      {combi && (
        <FilterGridItem>
          <SelectObject
            options={assignees}
            label="Organisatie"
            value={organisatie}
            onChange={(value) => handleFilterChange({ organisatie: value })}
          />
        </FilterGridItem>
      )}
      <FilterGridItem>
        <TextField
          variant="standard"
          value={startUitvoeringWeekVan}
          label={'Project gepland van'}
          fullWidth
          onChange={(e) =>
            handleFilterChange({ startUitvoeringWeekVan: e.target.value })
          }
          error={
            startUitvoeringWeekVan !== '' &&
            isNaN(parseISO(startUitvoeringWeekVan).getTime())
          }
          placeholder="bijv. 2023-W01"
        />
      </FilterGridItem>

      <FilterGridItem>
        <TextField
          variant="standard"
          value={startUitvoeringWeekTot}
          label={'Project gepland tot'}
          fullWidth
          onChange={(e) =>
            handleFilterChange({ startUitvoeringWeekTot: e.target.value })
          }
          error={
            startUitvoeringWeekTot !== '' &&
            isNaN(parseISO(startUitvoeringWeekTot).getTime())
          }
          placeholder="bijv. 2023-W01"
        />
      </FilterGridItem>

      <FilterGridItem>
        <SelectString
          multiple
          value={processtatus}
          options={processtappen}
          label="Processtap"
          onChange={(value) => handleFilterChange({ processtatus: value })}
        />
      </FilterGridItem>

      <FilterGridItem>
        <TextField
          fullWidth
          variant="standard"
          value={opleverweekVan}
          label={'Opleverweek van'}
          onChange={(e) =>
            handleFilterChange({ opleverweekVan: e.target.value })
          }
          error={
            opleverweekVan !== '' && isNaN(parseISO(opleverweekVan).getTime())
          }
          placeholder="bijv. 2023-W01"
        />
      </FilterGridItem>
      <FilterGridItem>
        <TextField
          fullWidth
          variant="standard"
          value={opleverweekTot}
          label={'Opleverweek tot'}
          onChange={(e) =>
            handleFilterChange({ opleverweekTot: e.target.value })
          }
          error={
            opleverweekTot !== '' && isNaN(parseISO(opleverweekTot).getTime())
          }
          placeholder="bijv. 2023-W01"
        />
      </FilterGridItem>

      <FilterGridItem>
        <SelectString
          multiple
          value={gemeente}
          options={gemeenten}
          label="Gemeente"
          onChange={(value) => handleFilterChange({ gemeente: value })}
        />
      </FilterGridItem>

      <FilterGridItem
        minColumns="full"
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <Tooltip title="Reset filters">
          <IconButton data-testid="clearbutton" onClick={handleReset}>
            <CloseIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="Zoeken">
          <IconButton data-testid="filterbutton" onClick={handleZoeken}>
            <SearchIcon />
          </IconButton>
        </Tooltip>

        <FilterMyList myList={myList} onFilterChange={handleFilterChange} />

        <LocalStorageButtons
          name={queryKey}
          storable={localFilters}
          onClickLoad={handleFilterChange}
        />
      </FilterGridItem>
    </FilterGridContainer>
  )
}
